// ============================================================
// CONSTANTS
// ============================================================
const BOARD_W_IN  = 9;
const BOARD_H_IN  = 36;
const OFFBOARD_IN = 1.0;

// ---- Throw tuning ----
// LENGTH_THRESH/SPEED_THRESH are tuned in raw screen pixels against a
// reference board size (REF_BOARD_H_PX — the original embed's board height
// at its old fixed cssScale of 16). The board itself now renders at very
// different sizes across devices (small phone vs. full desktop monitor), so
// computeLayout() rescales these every time it runs (see DYN_LENGTH_THRESH /
// DYN_SPEED_THRESH below) to keep the required swipe gesture feeling the
// same fraction of the board regardless of screen size. computeLanding()
// uses the DYN_* values, not these base ones directly.
const LENGTH_THRESH = 200;  // swipe length (px) for max throw — lower = less swipe needed = less controlled
const SPEED_THRESH  = 2.0;  // swipe speed (px/ms) for max throw — lower = slower flick allowed = less controlled
const REF_BOARD_H_PX = (36 + 2 * 1.0) * 16; // = 608px — board height these were tuned against
let DYN_LENGTH_THRESH = LENGTH_THRESH; // recomputed in computeLayout(); these
let DYN_SPEED_THRESH  = SPEED_THRESH;  // defaults hold until the first layout pass
// A touchscreen thumb-swipe physically covers less distance (and often a
// different speed profile) than a trackpad/mouse flick doing the "same"
// gesture, so touch throws get an extra boost on top of the board-size
// scaling above. Applied to touch-originated swipes only (checkTouchFlick),
// never to mouse/trackpad throws. 1.0 = no boost; raise it if mobile throws
// still fall short, lower it if they now overshoot / feel too easy.
const MOBILE_SWIPE_BOOST = 1.5;
const FLIGHT_MIN   = 2;   // shortest flight distance (board-inches) — minimum swipe
const FLIGHT_MAX   = 31;   // longest flight distance (board-inches) — maximum swipe
const BOUNCE_MIN   = 2;    // bounce distance on a soft throw (board-inches)
const BOUNCE_MAX   = 5;    // bounce distance on a hard throw (board-inches)
const SLIDE_MIN    = 3;    // slide distance on a soft throw (board-inches)
const SLIDE_MAX    = 5;    // slide distance on a hard throw (board-inches)
const BOUNCE_ANGLE_MAX = 4;  // max direction change per bounce (degrees) — 0 = straight, 75 = sharp veer
const HOLE_FUNNEL_G  = 1.9;   // hole gravity — higher = sucks in dice passing over earlier in slide (1=only slow dice, 3=most dice)
const BOUNCE_ROT_MIN = 15;    // min rotation added per bounce landing (degrees)
const BOUNCE_ROT_MAX = 35;    // max rotation added per bounce landing (degrees)
const FINAL_ROT_MAX  = 12;    // max resting angle — die settles near square (degrees)

// ---- Flight arc tuning ----
const THROW_HEIGHT = 2.0;    // die release height above board (board-inches)
const ARC_HEIGHT   = 4.5;    // additional arc height above board at peak (board-inches)
const PEG_HEIGHT   = 1.125;  // peg height above board surface (board-inches) — flight collision gate
// ----------------------

// ---- Peg collision tuning ----
const SLIDE_PEG_RESTITUTION = 0.4;  // 0–1: fraction of remaining slide distance kept after peg hit (lower = die stops sooner)
// ----------------------

// ---- Die-die collision tuning ----
const DIE_KICK_SCALE        = 0.4;  // 0–1: fraction of die1's normal momentum passed to die2 (0 = die2 doesn't move, 1 = full transfer)
const DIE_TANGENTIAL_RETAIN = 0.4;  // 0–1: fraction of die1's tangential (sideways) momentum kept after collision (1 = no friction, 0 = die1 stops completely)
const KISS_THRESHOLD        = 0.2;  // board-inches of remaining slide below which die1 just comes to rest against die2 (a "kiss") rather than bouncing
// ----------------------
// ----------------------

// ---- Sound tuning ----
// Bounce click (die hitting board):
const SND_BOUNCE_DUR   = 0.09; // seconds — shorter = sharper click, longer = more thud
const SND_BOUNCE_FREQ  = 800;   // Hz — higher = crisper/brighter, lower = thuddy/dull
const SND_BOUNCE_Q     = 40;     // resonance — higher = more tonal "tock", lower = broader noise thud
const SND_BOUNCE_VOL   = 8;   // peak volume (first bounce); each bounce decays by SND_BOUNCE_DECAY
const SND_BOUNCE_DECAY = 0.4;  // 0–1 — how much quieter each successive bounce is (lower = faster decay)
// Slide (die scraping board):
const SND_SLIDE_FREQ   = 1100;   // Hz — higher = hiss/metallic, lower = woody scrape
const SND_SLIDE_Q      = 6;   // resonance — higher = more focused tone, lower = broad hiss
const SND_SLIDE_VOL    = 0.004;  // peak volume of slide sound
// Peg hit (die deflecting off peg):
const SND_PEG_DUR      = 0.06;   // seconds — short sharp knock
const SND_PEG_FREQ     = 520;    // Hz — lower/woodier than die-on-board bounce
const SND_PEG_Q        = 18;     // resonance — tighter than bounce but not fully tonal
const SND_PEG_VOL      = 5;      // peak volume
// ----------------------

const ZONES_Y = {
  B_SCORE_END: 4.5,
  B_DEAD_END:  13.5,
  CENTER_END:  22.5,
  A_DEAD_END:  31.5,
};

const HOLES = {
  B: { x: 4.5, y: 2.25,  r: 0.5625 },
  A: { x: 4.5, y: 33.75, r: 0.5625 },
};

const CENTER_CIRCLE = { x: 4.5, y: 18, r_inner: 1.875, r_outer: 2.0 };
const CORNER_SIZE   = 1.5;
const DIE_SIZE_IN   = 0.625;
const DIE_HALF      = DIE_SIZE_IN / 2;
const PEG_HOLES     = 35;
const WIN_SCORE     = 68;

const COLORS = {
  offboard:   '#1e1e1e',
  b_score:    '#FFFFFF',
  b_dead:     '#D93025',
  center:     '#FFFFFF',
  a_dead:     '#1A6FD4',
  a_score:    '#FFFFFF',
  board_outline: '#222222',
};

// ============================================================
// LAYOUT / SCALE
// ============================================================
let SCALE, BOARD_X, BOARD_Y;
let canvas, ctx;

function computeLayout() {
  const dpr     = window.devicePixelRatio || 1;
  const modalEl = document.getElementById('zonez-modal');
  const mRect   = modalEl.getBoundingClientRect();
  // Modal is sized to exactly fill the viewport (100vw / 100dvh via CSS), so
  // its rect IS the available screen space — falls back to window size while
  // the modal is still display:none (rect collapses to 0) and harmlessly
  // clamps to a tiny scale until computeLayout() re-runs on open.
  const viewW = mRect.width  || window.innerWidth;
  const viewH = mRect.height || window.innerHeight;

  // Must match the @media max-width breakpoint in the <style> block above,
  // where the team panels shrink to a narrower version of the same format.
  const narrow = viewW <= 460;

  // ---- Width budget ----
  // Team panels sit beside the board at every screen size now (Team A left,
  // board middle, Team B right) — just narrower on small screens so the
  // board still gets most of the width instead of being squeezed out.
  const panelW = narrow ? 92 : 130;
  const gap    = 12;
  const hPad   = 24; // #game-container's own left/right padding
  const availW = viewW - (panelW + gap) * 2 - hPad;

  // ---- Height budget ----
  // Measure the actual rendered chrome around the canvas (header text, and
  // the throw-zone/buttons below the board) so the whole thing fits in
  // exactly one screen, no scrolling, on any device. This only produces real
  // numbers once the modal is open; computeLayout() is re-run right after
  // opening (see openZonezModal).
  const headerH = document.getElementById('zonez-header').getBoundingClientRect().height;
  const belowH  = document.getElementById('zonez-below-canvas').getBoundingClientRect().height;
  // #zonez-widget flex gap between header and game-container — slightly
  // bigger on mobile (must match the @media rule in the <style> block above).
  const widgetGap  = narrow ? 10 : 4;
  const wrapperGap = 4; // #canvas-wrapper flex gap between canvas and below-canvas chrome
  const safety = 12; // breathing room so nothing touches the screen edge
  const availH = viewH - headerH - belowH - widgetGap - wrapperGap - safety;

  const cssScaleW = availW / (BOARD_W_IN + 2 * OFFBOARD_IN);
  const cssScaleH = availH / (BOARD_H_IN + 2 * OFFBOARD_IN);
  const cssScale  = Math.max(1, Math.min(cssScaleW, cssScaleH, 40));
  SCALE = cssScale * dpr; // all drawing uses physical pixels

  const cssW = (BOARD_W_IN + 2 * OFFBOARD_IN) * cssScale;
  const cssH = (BOARD_H_IN + 2 * OFFBOARD_IN) * cssScale;
  canvas.width  = cssW * dpr; // physical pixel resolution
  canvas.height = cssH * dpr;
  canvas.style.width  = cssW + 'px'; // CSS display size unchanged
  canvas.style.height = cssH + 'px';
  BOARD_X = OFFBOARD_IN * SCALE;
  BOARD_Y = OFFBOARD_IN * SCALE;
  document.getElementById('canvas-wrapper').style.width = cssW + 'px';

  // Keep the swipe gesture feeling like the same fraction of the board
  // regardless of how big the board renders on this device (see the
  // LENGTH_THRESH/SPEED_THRESH comment above) — clamped so an extreme board
  // size can't make throws require an absurdly long/short swipe.
  const boardScaleFactor = Math.max(0.5, Math.min(cssH / REF_BOARD_H_PX, 3));
  DYN_LENGTH_THRESH = LENGTH_THRESH * boardScaleFactor;
  DYN_SPEED_THRESH  = SPEED_THRESH  * boardScaleFactor;
}

const bx = x => BOARD_X + x * SCALE;
const by = y => BOARD_Y + y * SCALE;
const bs = s => s * SCALE;

// ============================================================
// PEG TRACKS — two separate Z tracks, one per dead zone
// Team A's peg lives in Team B's Dead Zone (y: 4.5"–13.5"), starts top-left
// Team B's peg lives in Team A's Dead Zone (y: 22.5"–31.5"), starts bottom-right
// ============================================================
function buildPegTrackA() {
  const holes = [];
  const INSET_X = 0.5;
  const INSET_Y = 0.5;
  const leftX    = INSET_X;
  const rightX   = BOARD_W_IN - INSET_X;
  const topY     = ZONES_Y.B_SCORE_END + INSET_Y;   // 5.4"
  const botY     = ZONES_Y.B_DEAD_END  - INSET_Y;   // 12.6"
  const hSpacing = (rightX - leftX) / 10;

  // Top leg: left → right (holes 0–10)
  for (let i = 0; i < 11; i++) {
    holes.push({ x: leftX + i * hSpacing, y: topY });
  }
  // Diagonal: top-right → bottom-left (holes 11–23, exclusive of corners)
  for (let i = 1; i <= 13; i++) {
    const t = i / 14;
    holes.push({ x: rightX + t * (leftX - rightX), y: topY + t * (botY - topY) });
  }
  // Bottom leg: left → right (holes 24–34)
  for (let i = 0; i < 11; i++) {
    holes.push({ x: leftX + i * hSpacing, y: botY });
  }
  return holes;
}

function buildPegTrackB() {
  const holes = [];
  const INSET_X = 0.5;
  const INSET_Y = 0.5;
  const leftX    = INSET_X;
  const rightX   = BOARD_W_IN - INSET_X;
  const topY     = ZONES_Y.CENTER_END  + INSET_Y;
  const botY     = ZONES_Y.A_DEAD_END  - INSET_Y;
  const hSpacing = (rightX - leftX) / 10;

  // Bottom leg: right → left (holes 0–10), start = bottom-right
  for (let i = 0; i < 11; i++) {
    holes.push({ x: rightX - i * hSpacing, y: botY });
  }
  // Diagonal: bottom-left → top-right (holes 11–23, exclusive of corners)
  for (let i = 1; i <= 13; i++) {
    const t = i / 14;
    holes.push({ x: leftX + t * (rightX - leftX), y: botY + t * (topY - botY) });
  }
  // Top leg: right → left (holes 24–34)
  for (let i = 0; i < 11; i++) {
    holes.push({ x: rightX - i * hSpacing, y: topY });
  }
  return holes;
}

const PEG_TRACK_A = buildPegTrackA();
const PEG_TRACK_B = buildPegTrackB();

function scoreToPegIndex(score) {
  if (score <= 34) return score;
  return WIN_SCORE - score;  // 68 - score
}

// ============================================================
// GAME STATE
// ============================================================
const gameState = {
  currentTeam: 'A',
  phase: 'throwing',  // 'throwing' | 'animating' | 'showing' | 'gameOver' | 'resetting'
  diceThrown: 0,
  turnScore: 0,
  dieResults: [],      // [{points, label}] for current turn
  scores: { A: 0, B: 0 },
  winner: null,
  dice: [],            // landed dice on board [{x,y,value,label}]
  animDie: null,       // currently animating die
  redemption: false,   // true when opposing team gets their redemption throw
  redemptionTeam: null,
  _overlayTimer: null,
};

function resetGame() {
  gameState.currentTeam = 'A';
  gameState.phase = 'throwing';
  gameState.diceThrown = 0;
  gameState.turnScore = 0;
  gameState.dieResults = [];
  gameState.scores = { A: 0, B: 0 };
  gameState.winner = null;
  gameState.dice = [];
  gameState.animDie = null;
  gameState.redemption = false;
  gameState.redemptionTeam = null;
  gameState._overlayTimer = null;
  hideOverlay();
  applyBoardRotation();
  updateUI();
  renderAll();
}

// ============================================================
// ZONE & SCORING DETECTION
// ============================================================
function pointZone(xIn, yIn) {
  if (xIn < 0 || xIn > BOARD_W_IN || yIn < 0 || yIn > BOARD_H_IN) return 'offboard';
  if (yIn <= ZONES_Y.B_SCORE_END) return 'b_score';
  if (yIn <= ZONES_Y.B_DEAD_END)  return 'b_dead';
  if (yIn <= ZONES_Y.CENTER_END)  return 'center';
  if (yIn <= ZONES_Y.A_DEAD_END)  return 'a_dead';
  return 'a_score';
}

function distToCenter(xIn, yIn) {
  const dx = xIn - CENTER_CIRCLE.x;
  const dy = yIn - CENTER_CIRCLE.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function inCircle(xIn, yIn, r) { return distToCenter(xIn, yIn) <= r; }

function inHole(xIn, yIn, team) {
  const h = HOLES[team];
  const dx = xIn - h.x, dy = yIn - h.y;
  return Math.sqrt(dx * dx + dy * dy) <= h.r;
}

// Returns fraction (0–1) of die area (approximated as circle of radius DIE_HALF) overlapping the scoring hole
function dieHoleOverlapFrac(xIn, yIn, team) {
  const h = HOLES[team];
  const r1 = DIE_HALF, r2 = h.r;
  const d = Math.hypot(xIn - h.x, yIn - h.y);
  if (d >= r1 + r2) return 0;
  if (d <= r2 - r1) return 1;  // die (smaller) fully inside hole
  const cos1 = (d*d + r1*r1 - r2*r2) / (2*d*r1);
  const cos2 = (d*d + r2*r2 - r1*r1) / (2*d*r2);
  const a1 = r1*r1 * Math.acos(Math.max(-1, Math.min(1, cos1)));
  const a2 = r2*r2 * Math.acos(Math.max(-1, Math.min(1, cos2)));
  const a3 = 0.5 * Math.sqrt(Math.max(0, (-d+r1+r2)*(d+r1-r2)*(d-r1+r2)*(d+r1+r2)));
  return (a1 + a2 - a3) / (Math.PI * r1 * r1);
}

function inCorner(xIn, yIn, team) {
  // Die must be entirely inside the triangle: shrink threshold by full die width
  const threshold = CORNER_SIZE - DIE_SIZE_IN; // 1.5 - 0.625 = 0.875"
  if (team === 'A') {
    const bl = xIn + (BOARD_H_IN - yIn);
    const br = (BOARD_W_IN - xIn) + (BOARD_H_IN - yIn);
    return bl <= threshold || br <= threshold;
  } else {
    const tl = xIn + yIn;
    const tr = (BOARD_W_IN - xIn) + yIn;
    return tl <= threshold || tr <= threshold;
  }
}

function dieCorners(xIn, yIn, angleDeg) {
  const θ = (angleDeg || 0) * Math.PI / 180;
  const cos = Math.cos(θ), sin = Math.sin(θ);
  return [[-1,-1],[1,-1],[1,1],[-1,1]].map(([sx, sy]) => ({
    x: xIn + DIE_HALF * (sx * cos - sy * sin),
    y: yIn + DIE_HALF * (sx * sin + sy * cos),
  }));
}

function scoreDie(value, xIn, yIn, team, angleDeg) {
  // Returns { points, label, win, hanger, offBoard }

  // Center off board — die falls off, graphic hidden
  const onBoard = xIn >= 0 && xIn <= BOARD_W_IN && yIn >= 0 && yIn <= BOARD_H_IN;
  if (!onBoard) return { points: 0, label: 'Off Board', win: false, offBoard: true };

  const corners = dieCorners(xIn, yIn, angleDeg);

  // Board-edge hanger: any rotated corner past edge → ×2
  const hangL   = Math.max(0, ...corners.map(c => -c.x));
  const hangR   = Math.max(0, ...corners.map(c => c.x - BOARD_W_IN));
  const hangT   = Math.max(0, ...corners.map(c => -c.y));
  const hangB   = Math.max(0, ...corners.map(c => c.y - BOARD_H_IN));
  const maxHang = Math.max(hangL, hangR, hangT, hangB);
  const edgeHanger = maxHang > 0;

  // Hole hanger: die overlaps own hole but center is NOT inside it — ×2
  // (a die cannot be both in the hole and a hanger)
  const h = HOLES[team];
  const distToHole  = Math.sqrt((xIn - h.x) ** 2 + (yIn - h.y) ** 2);
  const holeHanger  = !inHole(xIn, yIn, team) && distToHole < h.r + DIE_HALF;

  const hanger = edgeHanger || holeHanger;
  const mult   = hanger ? 2 : 1;

  const hangerPrefix = pts => `Hanger! ${pts >= 0 ? '+' : ''}${pts}`;

  // Corner win
  if (inCorner(xIn, yIn, team)) {
    return { points: 0, label: 'CORNER WIN!', win: true };
  }

  // Scoring hole — center inside hole (cannot be a hanger)
  if (inHole(xIn, yIn, team)) {
    // Use effective remaining: account for points already scored this turn (not yet applied to gameState.scores)
    const _turnPts = gameState.dieResults.reduce((s, r) => s + r.points, 0);
    const _effectiveRemaining = WIN_SCORE - gameState.scores[team] - _turnPts;
    const _onLastLeg = _effectiveRemaining > 0 && _effectiveRemaining <= 10;
    if (_onLastLeg) return { points: _effectiveRemaining, label: 'Hole! WIN!', win: true };
    return { points: 10, label: 'Hole! +10', win: false };
  }

  // Zone-based scoring using rotated corners + center
  const diePoints = [...corners, { x: xIn, y: yIn }].map(({ x, y }) => {
    const cpx = Math.max(0, Math.min(BOARD_W_IN, x));
    const cpy = Math.max(0, Math.min(BOARD_H_IN, y));
    return { zone: pointZone(cpx, cpy), inCC: inCircle(x, y, CENTER_CIRCLE.r_inner) };
  });

  // Center circle: any point touching inner circle → +1 (overrides penalty)
  if (diePoints.some(p => p.inCC)) {
    const pts = 1 * mult;
    return { points: pts, label: hanger ? hangerPrefix(pts) : `Center +${pts}`, win: false, hanger };
  }

  // Penalty: any corner in center zone outside inner circle
  if (diePoints.some(p => p.zone === 'center' && !p.inCC)) {
    const pts = -value * mult;
    return { points: pts, label: hanger ? hangerPrefix(pts) : `Penalty ${pts}`, win: false, hanger };
  }

  // Own scoring zone: entire die in own zone
  const ownZone = team === 'A' ? 'a_score' : 'b_score';
  if (diePoints.every(p => p.zone === ownZone)) {
    const pts = value * mult;
    return { points: pts, label: hanger ? hangerPrefix(pts) : `+${pts}`, win: false, hanger };
  }

  return { points: 0, label: 'Dead — 0', win: false };
}

// ============================================================
// BOARD DRAWING
// ============================================================
function drawBoard() {
  // Off-board background
  ctx.fillStyle = COLORS.offboard;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Zone fills
  const zoneDefs = [
    { color: COLORS.b_score, y0: 0,                  y1: ZONES_Y.B_SCORE_END },
    { color: COLORS.b_dead,  y0: ZONES_Y.B_SCORE_END, y1: ZONES_Y.B_DEAD_END },
    { color: COLORS.center,  y0: ZONES_Y.B_DEAD_END,  y1: ZONES_Y.CENTER_END },
    { color: COLORS.a_dead,  y0: ZONES_Y.CENTER_END,  y1: ZONES_Y.A_DEAD_END },
    { color: COLORS.a_score, y0: ZONES_Y.A_DEAD_END,  y1: BOARD_H_IN },
  ];
  for (const z of zoneDefs) {
    ctx.fillStyle = z.color;
    ctx.fillRect(bx(0), by(z.y0), bs(BOARD_W_IN), bs(z.y1 - z.y0));
  }

  // Corner triangles — Team B (top, red)
  ctx.fillStyle = '#D93025';
  [[0, 0, CORNER_SIZE, 0, 0, CORNER_SIZE],
   [BOARD_W_IN, 0, BOARD_W_IN - CORNER_SIZE, 0, BOARD_W_IN, CORNER_SIZE]].forEach(([x1,y1,x2,y2,x3,y3]) => {
    ctx.beginPath(); ctx.moveTo(bx(x1),by(y1)); ctx.lineTo(bx(x2),by(y2)); ctx.lineTo(bx(x3),by(y3)); ctx.closePath(); ctx.fill();
  });

  // Corner triangles — Team A (bottom, blue)
  ctx.fillStyle = '#1A6FD4';
  [[0, BOARD_H_IN, CORNER_SIZE, BOARD_H_IN, 0, BOARD_H_IN - CORNER_SIZE],
   [BOARD_W_IN, BOARD_H_IN, BOARD_W_IN - CORNER_SIZE, BOARD_H_IN, BOARD_W_IN, BOARD_H_IN - CORNER_SIZE]].forEach(([x1,y1,x2,y2,x3,y3]) => {
    ctx.beginPath(); ctx.moveTo(bx(x1),by(y1)); ctx.lineTo(bx(x2),by(y2)); ctx.lineTo(bx(x3),by(y3)); ctx.closePath(); ctx.fill();
  });

  // Inner circle (white fill = center zone)
  ctx.beginPath();
  ctx.arc(bx(CENTER_CIRCLE.x), by(CENTER_CIRCLE.y), bs(CENTER_CIRCLE.r_inner), 0, Math.PI * 2);
  ctx.fillStyle = '#E0E0E0';
  ctx.fill();

  // Scoring holes
  for (const h of [HOLES.B, HOLES.A]) {
    ctx.beginPath();
    ctx.arc(bx(h.x), by(h.y), bs(h.r), 0, Math.PI * 2);
    ctx.fillStyle = COLORS.offboard;
    ctx.fill();
    ctx.strokeStyle = '#555';
    ctx.lineWidth = bs(0.05);
    ctx.stroke();
  }


  // Peg track dots on dead zones (Z shape)
  drawPegTrackDots();

  // Zone labels
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Board outline
  ctx.strokeStyle = COLORS.board_outline;
  ctx.lineWidth   = bs(0.08);
  ctx.strokeRect(bx(0), by(0), bs(BOARD_W_IN), bs(BOARD_H_IN));
}

function drawPegTrackDots() {
  const r = bs(0.07);
  for (const track of [PEG_TRACK_A, PEG_TRACK_B]) {
    for (const { x, y } of track) {
      ctx.beginPath();
      ctx.arc(bx(x), by(y), r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.fill();
    }
  }
}

function drawPegMarkers() {
  const r = bs(0.17);

  function pegDir(track, score) {
    if (score >= WIN_SCORE) return null;
    const idx = scoreToPegIndex(score);
    const nextIdx = score < 34 ? idx + 1 : idx - 1;
    if (nextIdx < 0 || nextIdx >= track.length) return null;
    const cur = track[idx], nxt = track[nextIdx];
    const dx = nxt.x - cur.x, dy = nxt.y - cur.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    return { dx: dx / len, dy: dy / len };
  }

  function drawArrowOnPeg(cx, cy, dir) {
    if (!dir) return;
    const totalLen = r * 7.0;   // total arrow length from peg center
    const headLen  = r * 1.6;   // arrowhead triangle length
    const headHalf = r * 0.85;  // arrowhead half-width at base
    const stemStart = r * 1.15; // just past peg edge
    const px = -dir.dy, py = dir.dx;
    const tipX      = cx + dir.dx * totalLen;
    const tipY      = cy + dir.dy * totalLen;
    const headBaseX = cx + dir.dx * (totalLen - headLen);
    const headBaseY = cy + dir.dy * (totalLen - headLen);
    const stemX     = cx + dir.dx * stemStart;
    const stemY     = cy + dir.dy * stemStart;

    // Stem
    ctx.beginPath();
    ctx.moveTo(stemX, stemY);
    ctx.lineTo(headBaseX, headBaseY);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = bs(0.045);
    ctx.stroke();

    // Arrowhead
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(headBaseX + px * headHalf, headBaseY + py * headHalf);
    ctx.lineTo(headBaseX - px * headHalf, headBaseY - py * headHalf);
    ctx.closePath();
    ctx.fillStyle = '#000';
    ctx.fill();
  }

  // Team A peg (blue) — on Team A's track in Team B's dead zone
  {
    const h = PEG_TRACK_A[scoreToPegIndex(gameState.scores.A)];
    ctx.beginPath();
    ctx.arc(bx(h.x), by(h.y), r, 0, Math.PI * 2);
    ctx.fillStyle = '#4488ff';
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = bs(0.04);
    ctx.stroke();
    drawArrowOnPeg(bx(h.x), by(h.y), pegDir(PEG_TRACK_A, gameState.scores.A));
  }

  // Team B peg (red) — on Team B's track in Team A's dead zone
  {
    const h = PEG_TRACK_B[scoreToPegIndex(gameState.scores.B)];
    ctx.beginPath();
    ctx.arc(bx(h.x), by(h.y), r, 0, Math.PI * 2);
    ctx.fillStyle = '#ff4444';
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = bs(0.04);
    ctx.stroke();
    drawArrowOnPeg(bx(h.x), by(h.y), pegDir(PEG_TRACK_B, gameState.scores.B));
  }
}

function drawDie(xIn, yIn, value, alpha, zIn, angleDeg) {
  const px   = bx(xIn);
  const py   = by(yIn) - bs(zIn || 0) * 0.55; // perspective lift: higher z = higher on screen
  const sz   = bs(DIE_SIZE_IN);
  const half = sz / 2;
  const rad  = sz * 0.18;

  ctx.save();
  ctx.globalAlpha = alpha ?? 1.0;
  ctx.translate(px, py);
  if (angleDeg) ctx.rotate(angleDeg * Math.PI / 180);

  ctx.fillStyle = '#FFFDF0';
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(-half, -half, sz, sz, rad);
  } else {
    ctx.rect(-half, -half, sz, sz);
  }
  ctx.fill();
  ctx.strokeStyle = '#333';
  ctx.lineWidth = Math.max(0.5, bs(0.04));
  ctx.stroke();

  // Pips — coords relative to die center (after translate)
  const pipR = sz * 0.10;
  const off  = half * 0.58;
  const layouts = {
    1: [[0,0]],
    2: [[-1,-1],[1,1]],
    3: [[-1,-1],[0,0],[1,1]],
    4: [[-1,-1],[1,-1],[-1,1],[1,1]],
    5: [[-1,-1],[1,-1],[0,0],[-1,1],[1,1]],
    6: [[-1,-1],[1,-1],[-1,0],[1,0],[-1,1],[1,1]],
  };
  ctx.fillStyle = '#1a1a1a';
  for (const [ox,oy] of (layouts[value] || [])) {
    ctx.beginPath();
    ctx.arc(ox * off, oy * off, pipR, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function drawScoreLabel(xIn, yIn, label, color, secondLine) {
  const fontSize = Math.round(bs(0.75));
  const px = bx(xIn);
  const offset = bs(DIE_SIZE_IN) + bs(0.2) + (secondLine ? fontSize : 0);
  ctx.font = `bold ${fontSize}px Georgia`;
  ctx.textAlign = 'center';
  ctx.fillStyle = color || '#fff';
  ctx.strokeStyle = 'rgba(0,0,0,0.8)';
  ctx.lineWidth = 3;
  ctx.save();

  if (gameState.currentTeam === 'A') {
    // Canvas is CSS-rotated 180° — rotate text to match
    const py = by(yIn) - offset;
    ctx.textBaseline = 'bottom';
    ctx.translate(px, py);
    ctx.rotate(Math.PI);
    ctx.strokeText(label, 0, 0);
    ctx.fillText(label, 0, 0);
  } else {
    // Draw above die; if that clips the top edge, flip below instead
    const pyAbove = by(yIn) - offset;
    if (pyAbove - fontSize < 0) {
      ctx.textBaseline = 'top';
      const pyBelow = by(yIn) + bs(DIE_SIZE_IN) + bs(0.2) + (secondLine ? fontSize : 0);
      ctx.strokeText(label, px, pyBelow);
      ctx.fillText(label, px, pyBelow);
    } else {
      ctx.textBaseline = 'bottom';
      ctx.strokeText(label, px, pyAbove);
      ctx.fillText(label, px, pyAbove);
    }
  }

  ctx.restore();
}

// ============================================================
// AUDIO
// ============================================================
let _audioCtx = null;

function getAC() {
  if (!_audioCtx) {
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    console.log('[audio] context created, state:', _audioCtx.state);
  }
  return _audioCtx;
}

function primeAudio() {
  const ac = getAC();
  if (ac.state === 'running') return;
  ac.resume()
    .then(() => console.log('[audio] resumed, state:', ac.state))
    .catch(err => console.error('[audio] resume failed:', err));
  // Belt-and-suspenders unlock: some mobile browsers (older iOS Safari in
  // particular) don't fully unlock audio output from resume() alone until a
  // sound has actually been started from within a user gesture.
  try {
    const buffer = ac.createBuffer(1, 1, ac.sampleRate);
    const src = ac.createBufferSource();
    src.buffer = buffer;
    src.connect(ac.destination);
    src.start(0);
  } catch (err) { console.error('[audio] silent unlock buffer failed:', err); }
}

function playStartTune() {
  const ac = getAC();
  const now = ac.currentTime;
  const notes = [
    { freq: 293.7, time: 0.00, dur: 0.18 },
    { freq: 369.9, time: 0.16, dur: 0.18 },
    { freq: 440.0, time: 0.30, dur: 0.20 },
    { freq: 493.9, time: 0.46, dur: 0.22 },
    { freq: 587.3, time: 0.64, dur: 0.55 },
  ];
  notes.forEach(({ freq, time, dur }) => _marimbaNote(ac, freq, now + time, dur));
  [880, 1174.7].forEach((freq, i) => _shimmer(ac, freq, now + 0.64 + i * 0.06));
}

function _marimbaNote(ac, freq, time, dur) {
  const osc1 = ac.createOscillator(), osc2 = ac.createOscillator();
  const gain = ac.createGain(), g2 = ac.createGain();
  osc1.type = 'sine';  osc1.frequency.value = freq;
  osc2.type = 'triangle'; osc2.frequency.value = freq * 2.756;
  g2.gain.value = 0.06;
  gain.gain.setValueAtTime(0, time);
  gain.gain.linearRampToValueAtTime(0.38, time + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.001, time + dur + 0.3);
  osc1.connect(gain); osc2.connect(g2); g2.connect(gain); gain.connect(ac.destination);
  osc1.start(time); osc1.stop(time + dur + 0.35);
  osc2.start(time); osc2.stop(time + dur + 0.35);
}

function _shimmer(ac, freq, time) {
  const osc = ac.createOscillator(), gain = ac.createGain();
  osc.type = 'sine'; osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, time);
  gain.gain.linearRampToValueAtTime(0.06, time + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.5);
  osc.connect(gain); gain.connect(ac.destination);
  osc.start(time); osc.stop(time + 0.55);
}

function playBounceSound(bounceIndex) {
  try {
    const ac        = getAC();
    const now       = ac.currentTime;
    const intensity = Math.pow(SND_BOUNCE_DECAY, bounceIndex);
    const dur       = SND_BOUNCE_DUR;

    const frames = Math.ceil(ac.sampleRate * dur);
    const buf    = ac.createBuffer(1, frames, ac.sampleRate);
    const data   = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;

    const src = ac.createBufferSource();
    src.buffer = buf;

    // Tight bandpass gives wooden-click character without sounding tonal
    const bpf = ac.createBiquadFilter();
    bpf.type = 'bandpass';
    bpf.frequency.value = SND_BOUNCE_FREQ;
    bpf.Q.value = SND_BOUNCE_Q;

    const gain = ac.createGain();
    gain.gain.setValueAtTime(intensity * SND_BOUNCE_VOL, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + dur);

    src.connect(bpf);
    bpf.connect(gain);
    gain.connect(ac.destination);
    src.start(now);
    src.stop(now + dur);
  } catch (e) { console.error('[audio] bounce error:', e); }
}

function playSlideSound(durationMs) {
  try {
    const ac  = getAC();
    const dur = Math.max(0.2, durationMs / 1000);
    const now = ac.currentTime;

    const frames = Math.ceil(ac.sampleRate * dur);
    const buf    = ac.createBuffer(1, frames, ac.sampleRate);
    const data   = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;

    const src = ac.createBufferSource();
    src.buffer = buf;

    // Lower bandpass = rougher wooden scrape, not metallic hiss
    const bpf = ac.createBiquadFilter();
    bpf.type = 'bandpass';
    bpf.frequency.value = SND_SLIDE_FREQ;
    bpf.Q.value = SND_SLIDE_Q;

    const gain = ac.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(SND_SLIDE_VOL, now + 0.04);
    gain.gain.setValueAtTime(SND_SLIDE_VOL, now + dur * 0.55);
    gain.gain.exponentialRampToValueAtTime(0.001, now + dur); // fade out as die slows

    src.connect(bpf);
    bpf.connect(gain);
    gain.connect(ac.destination);
    src.start(now);
    src.stop(now + dur);
  } catch (e) { console.error('[audio] slide error:', e); }
}

function playPegHitSound(deflectDist) {
  try {
    const ac  = getAC();
    const now = ac.currentTime;
    const dur = SND_PEG_DUR;
    // Scale volume by deflection distance (proxy for collision speed); SLIDE_MAX is the reference
    const volScale = Math.max(0.15, Math.min(1, deflectDist / SLIDE_MAX));

    const frames = Math.ceil(ac.sampleRate * dur);
    const buf    = ac.createBuffer(1, frames, ac.sampleRate);
    const data   = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;

    const src = ac.createBufferSource();
    src.buffer = buf;

    const bpf = ac.createBiquadFilter();
    bpf.type = 'bandpass';
    bpf.frequency.value = SND_PEG_FREQ;
    bpf.Q.value = SND_PEG_Q;

    const gain = ac.createGain();
    gain.gain.setValueAtTime(SND_PEG_VOL * volScale, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + dur);

    src.connect(bpf);
    bpf.connect(gain);
    gain.connect(ac.destination);
    src.start(now);
    src.stop(now + dur);
  } catch (e) { console.error('[audio] peg hit error:', e); }
}

function playGoodThrowSound() {
  try {
    const ac = getAC();
    _marimbaNote(ac, 587.3, ac.currentTime, 0.18);
  } catch (e) { console.error('[audio] good throw error:', e); }
}

function playWinTune() {
  try {
    const ac = getAC();
    const now = ac.currentTime;
    // D pentatonic climb: D4 → F#4 → A4 → B4 → D5
    [
      { freq: 293.7, time: 0.00, dur: 0.10 },
      { freq: 369.9, time: 0.08, dur: 0.10 },
      { freq: 440.0, time: 0.15, dur: 0.10 },
      { freq: 493.9, time: 0.22, dur: 0.10 },
      { freq: 587.3, time: 0.29, dur: 0.28 },
    ].forEach(({ freq, time, dur }) => {
      const osc1 = ac.createOscillator(), osc2 = ac.createOscillator();
      const gain = ac.createGain(), g2 = ac.createGain();
      const t = now + time;
      osc1.type = 'sine';     osc1.frequency.value = freq;
      osc2.type = 'triangle'; osc2.frequency.value = freq * 2.756;
      g2.gain.value = 0.06;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.32, t + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.001, t + dur + 0.4);
      osc1.connect(gain); osc2.connect(g2); g2.connect(gain); gain.connect(ac.destination);
      osc1.start(t); osc1.stop(t + dur + 0.45);
      osc2.start(t); osc2.stop(t + dur + 0.45);
    });
  } catch (e) { console.error('[audio] win tune error:', e); }
}

// ============================================================
// ANIMATION
// ============================================================
let animFrame = null;

function multiBounceHeight(t, numBounces, initialH) {
  // t in [0,1] — produces N geometrically diminishing half-sine bounces
  // Each bounce is half the height and half the duration of the previous
  t = Math.max(0, Math.min(1, t));
  const decay    = 0.5;
  const firstSeg = (1 - decay) / (1 - Math.pow(decay, numBounces)); // segment durations sum to 1
  let segStart = 0;
  for (let i = 0; i < numBounces; i++) {
    const segLen = firstSeg * Math.pow(decay, i);
    if (t <= segStart + segLen || i === numBounces - 1) {
      const localT = Math.max(0, Math.min(1, (t - segStart) / segLen));
      return initialH * Math.pow(decay, i) * Math.sin(localT * Math.PI);
    }
    segStart += segLen;
  }
  return 0;
}

function animateDieThrow(die, onDone) {
  // die: { startX, startY, initX, initY, rollX, rollY, endX, endY, numBounces, initialBounceH, value }
  const flightDist      = Math.hypot(die.initX - die.startX, die.initY - die.startY);
  const bounceDist      = Math.hypot(die.rollX - die.initX, die.rollY - die.initY);
  const slideDist       = Math.hypot(die.endX - die.rollX, die.endY - die.rollY);
  const FLIGHT_DUR      = Math.round(flightDist / 35 * 1000); // 35 in/s
  const BOUNCE_ROLL_DUR = Math.max(200, Math.round(bounceDist / 35 * 1000)); // same speed as flight
  const SLIDE_DUR       = Math.max(300, Math.round(slideDist / 12 * 1000));  // 12 in/s — always slower
  const TOTAL_DUR       = FLIGHT_DUR + BOUNCE_ROLL_DUR + SLIDE_DUR;

  // Pre-generate a random face per bounce; final bounce shows the real value
  const bounceValues = Array.from({ length: die.numBounces }, (_, i) =>
    i === die.numBounces - 1 ? die.value : Math.ceil(Math.random() * 6)
  );

  function getBounceSegment(t) {
    const decay    = 0.5;
    const firstSeg = (1 - decay) / (1 - Math.pow(decay, die.numBounces));
    let segStart   = 0;
    for (let i = 0; i < die.numBounces; i++) {
      const segLen = firstSeg * Math.pow(decay, i);
      if (t <= segStart + segLen || i === die.numBounces - 1) {
        return { segIdx: i, localT: Math.max(0, Math.min(1, (t - segStart) / segLen)) };
      }
      segStart += segLen;
    }
    return { segIdx: die.numBounces - 1, localT: 1 };
  }

  const start = performance.now();
  let lastSegIdx         = -1;
  let slideStarted       = false;
  let pegHitPlayed       = false;
  let dieKickFired       = false;
  let bouncePegSndPlayed  = false;
  let bounceDieKickFired  = false;

  function tick(now) {
    const elapsed = now - start;
    let xIn, yIn, zIn = 0, displayVal = die.value, angleDeg = 0;

    if (elapsed <= FLIGHT_DUR) {
      // Phase 1: Parabolic arc through air — die pre-spins toward first landing angle
      const t    = elapsed / FLIGHT_DUR;
      xIn = die.startX + (die.initX - die.startX) * t;
      yIn = die.startY + (die.initY - die.startY) * t;
      zIn = THROW_HEIGHT * (1 - t) + ARC_HEIGHT * 4 * t * (1 - t);
      angleDeg = t * die.bounceAngles[0];
    } else if (elapsed <= FLIGHT_DUR + BOUNCE_ROLL_DUR) {
      // Phase 2: Bounce + Roll — N diminishing bounces while die travels forward
      const t    = (elapsed - FLIGHT_DUR) / BOUNCE_ROLL_DUR;
      const { segIdx, localT } = getBounceSegment(t);
      if (segIdx !== lastSegIdx) {
        playBounceSound(segIdx);
        lastSegIdx = segIdx;
      }
      const wpA = die.bounceWaypoints[segIdx];
      const wpB = die.bounceWaypoints[segIdx + 1];
      // If this segment has a peg or die-die contact point, split the animation there
      const _bph = die.bouncePegHit, _bdh = die.bounceDieHit;
      const _hit = (_bph && segIdx === _bph.segIdx) ? _bph
                 : (_bdh && segIdx === _bdh.segIdx) ? _bdh : null;
      if (_hit) {
        if (localT <= _hit.segT) {
          const sub = _hit.segT > 0 ? localT / _hit.segT : 0;
          xIn = wpA.x + (_hit.hx - wpA.x) * sub;
          yIn = wpA.y + (_hit.hy - wpA.y) * sub;
        } else {
          // First frame past contact — fire sound and (for die hit) kick animation
          if (_hit === _bph && !bouncePegSndPlayed) {
            playPegHitSound(_bph.deflectDist);
            bouncePegSndPlayed = true;
          }
          if (_hit === _bdh && !bounceDieKickFired) {
            playPegHitSound(_bdh.deflectDist);
            animateDieKick(_bdh.kickedDie);
            bounceDieKickFired = true;
          }
          const sub = _hit.segT < 1 ? (localT - _hit.segT) / (1 - _hit.segT) : 1;
          xIn = _hit.hx + (wpB.x - _hit.hx) * sub;
          yIn = _hit.hy + (wpB.y - _hit.hy) * sub;
        }
      } else {
        xIn = wpA.x + (wpB.x - wpA.x) * localT;
        yIn = wpA.y + (wpB.y - wpA.y) * localT;
      }
      zIn = multiBounceHeight(t, die.numBounces, die.initialBounceH);
      displayVal = bounceValues[segIdx];
      // Interpolate rotation between bounce landing angles
      angleDeg = die.bounceAngles[segIdx] + (die.bounceAngles[segIdx + 1] - die.bounceAngles[segIdx]) * localT;
    } else {
      // Phase 3: Slide — die flat on board, friction decelerates to rest
      if (!slideStarted) {
        // Play scrape only up to the first split point (peg or die collision)
        const _firstSplit = [die.slideHitT, die.dieKickT].filter(x => x !== null).sort((a,b) => a-b)[0];
        const approachMs = _firstSplit != null ? SLIDE_DUR * _firstSplit : SLIDE_DUR;
        playSlideSound(approachMs);
        slideStarted = true;
      }
      const t = Math.min((elapsed - FLIGHT_DUR - BOUNCE_ROLL_DUR) / SLIDE_DUR, 1);
      // Build ordered list of split points: each is { t, fromX, fromY, toX, toY, sound? }
      // Segments: rollLand → [peg hit?] → [die contact?] → endX
      const _segPts = [{ t: 0, x: die.rollX, y: die.rollY }];
      if (die.slideHitT !== null)
        _segPts.push({ t: die.slideHitT, x: die.slideHitX, y: die.slideHitY, pegSound: true });
      if (die.dieKickT !== null)
        _segPts.push({ t: die.dieKickT, x: die.kickedDie.contactX, y: die.kickedDie.contactY, dieSound: true });
      _segPts.push({ t: 1, x: die.endX, y: die.endY });
      _segPts.sort((a, b) => a.t - b.t);

      // Fire sounds and die-kick at transition points
      for (let _si = 1; _si < _segPts.length - 1; _si++) {
        const _sp = _segPts[_si];
        if (_sp.pegSound && !pegHitPlayed && t >= _sp.t) {
          playPegHitSound(die.pegDeflectDist);
          const remainMs = SLIDE_DUR * (1 - _sp.t);
          playSlideSound(remainMs);
          pegHitPlayed = true;
        }
        if (_sp.dieSound && !dieKickFired && t >= _sp.t) {
          dieKickFired = true;
          playPegHitSound(die.kickedDie.dist);
          animateDieKick(die.kickedDie);
        }
      }

      // Find current segment and interpolate
      let _curSeg = null;
      for (let _si = 0; _si < _segPts.length - 1; _si++) {
        if (t <= _segPts[_si + 1].t) { _curSeg = _si; break; }
      }
      if (_curSeg === null) _curSeg = _segPts.length - 2;
      const _sa = _segPts[_curSeg], _sb = _segPts[_curSeg + 1];
      const _span = _sb.t - _sa.t;
      const _localT = _span > 0 ? Math.min((t - _sa.t) / _span, 1) : 1;
      const ease = 1 - Math.pow(1 - _localT, 4);
      xIn = _sa.x + (_sb.x - _sa.x) * ease;
      yIn = _sa.y + (_sb.y - _sa.y) * ease;
      angleDeg = die.bounceAngles[die.numBounces] + (die.finalAngle - die.bounceAngles[die.numBounces]) * (1 - Math.pow(1 - t, 4));
    }

    renderAll(xIn, yIn, displayVal, zIn, angleDeg);

    if (elapsed < TOTAL_DUR) {
      animFrame = requestAnimationFrame(tick);
    } else {
      animFrame = null;
      onDone();
    }
  }
  animFrame = requestAnimationFrame(tick);
}

function animateDieKick(kickData) {
  const { idx, fromX, fromY, toX, toY, dist } = kickData;
  if (dist < 0.001) return;
  const dur = Math.max(150, Math.round(dist / 10 * 1000));
  playSlideSound(dur);
  const kickStart = performance.now();
  function kickTick(now) {
    const t    = Math.min((now - kickStart) / dur, 1);
    const ease = 1 - Math.pow(1 - t, 4);
    gameState.dice[idx].x = fromX + (toX - fromX) * ease;
    gameState.dice[idx].y = fromY + (toY - fromY) * ease;
    // Only render here when main animation is done (otherwise its tick already renders each frame)
    if (animFrame === null) renderAll();
    if (t < 1) {
      requestAnimationFrame(kickTick);
    } else {
      gameState.dice[idx].x = toX;
      gameState.dice[idx].y = toY;
      if (kickData.pegHit) playPegHitSound(kickData.dist);
      // Update die2's visual label to reflect its new scored position
      const d2 = gameState.dice[idx];
      if (d2.team) {
        const newScore = scoreDie(d2.value, toX, toY, d2.team, d2.angle);
        d2.label      = newScore.label;
        d2.labelColor = newScore.hanger ? '#ffdd44' : newScore.points > 0 ? '#44ff88' : newScore.points < 0 ? '#ff6644' : '#cccccc';
        d2.offBoard   = !!newScore.offBoard;
      }
      if (animFrame === null) renderAll();
    }
  }
  requestAnimationFrame(kickTick);
}

// ============================================================
// RENDER
// ============================================================
function drawDieShadow(xIn, yIn, zIn) {
  const px   = bx(xIn);
  const py   = by(yIn);
  const half = bs(DIE_SIZE_IN) / 2;
  const opacity = 0.45 * Math.max(0, 1 - zIn / 7);
  const scaleY  = Math.max(0.12, 0.8 - zIn * 0.09);
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.ellipse(px, py, half * 0.85, half * scaleY, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function renderAll(animX, animY, animVal, animZ, animAngle) {
  drawBoard();
  drawPegMarkers();

  // Draw landed dice
  for (const d of gameState.dice) {
    if (!d.offBoard) drawDie(d.x, d.y, d.value, 1.0, 0, d.angle || 0);
    if (d.label) {
      // Off-board label: clamp to board edge so it's always visible
      const lx = Math.max(1.5, Math.min(BOARD_W_IN - 1.5, d.x));
      const ly = Math.max(DIE_HALF, Math.min(BOARD_H_IN - DIE_HALF, d.y));
      drawScoreLabel(lx, ly, d.label, d.labelColor);
      if (d.kiss && !d.offBoard) drawScoreLabel(lx, ly, 'A KISS', '#ffcc44', true);
    }
  }

  // Draw animating die with arc shadow + height offset
  if (animX !== undefined) {
    if (animZ > 0) drawDieShadow(animX, animY, animZ);
    drawDie(animX, animY, animVal, 1.0, animZ, animAngle || 0);
  }
}

// ============================================================
// THROW MECHANICS
// ============================================================
function throwOrigin(team) {
  // Team A throws from top (y=-2"), Team B from bottom (y=38")
  const x = BOARD_W_IN / 2 + (Math.random() - 0.5) * 2;
  const y = team === 'A' ? -2.0 : BOARD_H_IN + 2.0;
  return { x, y };
}

function computeLanding(swipeDx, swipeDy, dt, team) {
  const swipeLen = Math.sqrt(swipeDx * swipeDx + swipeDy * swipeDy);
  if (swipeLen < 5) return null;

  const swipeSpeed  = swipeLen / Math.max(dt, 50);                        // px/ms
  const lengthNorm  = Math.min(swipeLen / DYN_LENGTH_THRESH, 1.0);        // 0–1 from length alone
  const velocityNorm = Math.min(swipeSpeed / DYN_SPEED_THRESH, 1.0);      // 0–1 from speed alone
  const speedNorm   = Math.min((lengthNorm + velocityNorm) / 2, 1.0);     // average of length and speed
  const clamped    = FLIGHT_MIN + speedNorm * (FLIGHT_MAX - FLIGHT_MIN);

  const dirX = swipeDx / swipeLen;
  const dirY = swipeDy / swipeLen;

  const origin = throwOrigin(team);
  // Lateral deviation: ±1.5 die width
  const lat  = (Math.random() - 0.5) * DIE_SIZE_IN * 3;
  const perpX = -dirY;
  const perpY =  dirX;

  let landX = origin.x + dirX * clamped + perpX * lat;
  let landY = origin.y + dirY * clamped + perpY * lat;

  // Throw direction for roll
  const pathDx  = landX - origin.x, pathDy = landY - origin.y;
  const pathLen = Math.sqrt(pathDx * pathDx + pathDy * pathDy);
  let pdirX   = pathDx / pathLen, pdirY = pathDy / pathLen;
  let rollDirX  = pdirX, rollDirY = pdirY;
  let rollPerpX = -rollDirY, rollPerpY = rollDirX;

  // Both pegs — own and opponent — checked in flight, bounce, and slide
  const oppTrack = team === 'A' ? PEG_TRACK_B : PEG_TRACK_A;
  const oppScore = team === 'A' ? gameState.scores.B : gameState.scores.A;
  const ownTrack = team === 'A' ? PEG_TRACK_A : PEG_TRACK_B;
  const ownScore = team === 'A' ? gameState.scores.A : gameState.scores.B;
  const peg      = oppTrack[scoreToPegIndex(oppScore)];  // kept for debug/return compat
  const pegs     = [peg, ownTrack[scoreToPegIndex(ownScore)]];
  const PEG_R    = 0.17; // matches visual peg radius in drawPegMarkers
  const collideR = PEG_R + DIE_HALF;
  const pegCollisions = [];

  // Flight peg collision — find earliest peg hit where arc is below peg height
  { let _bestFt = null, _bestPeg = null;
    for (const _p of pegs) {
      const _ft = segmentCircleHit(origin.x, origin.y, landX, landY, _p.x, _p.y, collideR);
      if (_ft !== null && (_bestFt === null || _ft < _bestFt)) { _bestFt = _ft; _bestPeg = _p; }
    }
    if (_bestFt !== null) {
      const arcH = THROW_HEIGHT * (1 - _bestFt) + ARC_HEIGHT * 4 * _bestFt * (1 - _bestFt);
      if (arcH < PEG_HEIGHT) {
        const hx = origin.x + _bestFt * (landX - origin.x), hy = origin.y + _bestFt * (landY - origin.y);
        const nx = hx - _bestPeg.x, ny = hy - _bestPeg.y, nl = Math.sqrt(nx*nx + ny*ny);
        const nnx = nx/nl, nny = ny/nl;
        const rx = landX - hx, ry = landY - hy;
        const dot = rx*nnx + ry*nny;
        landX = hx + rx - 2*dot*nnx;
        landY = hy + ry - 2*dot*nny;
        // Reflect the throw direction so bounce + slide continue in the deflected heading
        const _fdot = pdirX*nnx + pdirY*nny;
        pdirX = pdirX - 2*_fdot*nnx;
        pdirY = pdirY - 2*_fdot*nny;
        pegCollisions.push({ phase: 'flight', hx, hy, t: _bestFt });
      }
    }
  }

  // (no x/y clamp — die can land off board)

  const initialLandX = landX, initialLandY = landY;

  let numBounces       = speedNorm >= 0.5 ? 4 : 3;
  const initialBounceH = 0.7 + 0.35 * speedNorm;

  // Pre-compute per-bounce rotation deltas — used for both visual spin and steering
  const rotDeltas = [];
  for (let i = 0; i < numBounces; i++) {
    const rotSign = Math.random() < 0.5 ? 1 : -1;
    rotDeltas.push(rotSign * (BOUNCE_ROT_MIN + Math.random() * (BOUNCE_ROT_MAX - BOUNCE_ROT_MIN)));
  }

  // Roll: N bounces, each steered by die spin — direction rotates 0–BOUNCE_ANGLE_MAX° per bounce
  const rollDist   = (BOUNCE_MIN + speedNorm * (BOUNCE_MAX - BOUNCE_MIN)) * (0.8 + Math.random() * 0.4);
  const _bDecay    = 0.5;
  const _bFirst    = (1 - _bDecay) / (1 - Math.pow(_bDecay, numBounces));
  const bounceWaypoints = [{ x: landX, y: landY }];
  const bounceLaterals  = [];
  let curX = landX, curY = landY;
  for (let i = 0; i < numBounces; i++) {
    const segFrac = _bFirst * Math.pow(_bDecay, i);
    // Angle change: scales with spin magnitude (bigger spin = bigger veer), randomised 50–100%
    const spinFrac   = Math.abs(rotDeltas[i]) / BOUNCE_ROT_MAX;
    const angleDelta = Math.sign(rotDeltas[i]) * spinFrac * BOUNCE_ANGLE_MAX * (0.5 + 0.5 * Math.random());
    bounceLaterals.push(angleDelta);
    // Rotate current heading by angleDelta
    const rad = angleDelta * Math.PI / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    const newDirX = rollDirX * cos - rollDirY * sin;
    const newDirY = rollDirX * sin + rollDirY * cos;
    rollDirX = newDirX; rollDirY = newDirY;
    rollPerpX = -rollDirY; rollPerpY = rollDirX;
    curX += rollDirX * rollDist * segFrac;
    curY += rollDirY * rollDist * segFrac;
    bounceWaypoints.push({ x: curX, y: curY });
  }
  let rollLandX = curX;
  let rollLandY = curY;
  bounceWaypoints[bounceWaypoints.length - 1] = { x: rollLandX, y: rollLandY };

  // Peg collision on bounce waypoints — deflect landing destination, no extra waypoint inserted
  // (inserting a waypoint mid-arc forces the die to "land" at the peg while still airborne)
  for (let i = 0; i < bounceWaypoints.length - 1; i++) {
    const wa = bounceWaypoints[i], wb = bounceWaypoints[i + 1];
    let _bestT = null, _bestPeg = null;
    for (const _p of pegs) {
      const _t = segmentCircleHit(wa.x, wa.y, wb.x, wb.y, _p.x, _p.y, collideR);
      if (_t !== null && (_bestT === null || _t < _bestT)) { _bestT = _t; _bestPeg = _p; }
    }
    if (_bestT !== null) {
      const hx = wa.x + _bestT * (wb.x - wa.x), hy = wa.y + _bestT * (wb.y - wa.y);
      const nx = hx - _bestPeg.x, ny = hy - _bestPeg.y, nl = Math.sqrt(nx*nx + ny*ny);
      const nnx = nx/nl, nny = ny/nl;
      // Incoming direction (wa → hit point, normalized)
      const _idist = Math.hypot(hx - wa.x, hy - wa.y);
      const _iux = (hx - wa.x) / _idist, _iuy = (hy - wa.y) / _idist;
      // Reflected direction
      const _idot = _iux * nnx + _iuy * nny;
      const _rux = _iux - 2 * _idot * nnx, _ruy = _iuy - 2 * _idot * nny;
      // Perpendicular axes for frame rotation
      const _ipx = -_iuy, _ipy = _iux;
      const _rpx = -_ruy, _rpy = _rux;
      // Deflect wb in place — no splice, arc height stays smooth
      const _rsx = wb.x - hx, _rsy = wb.y - hy;
      const _rdot = _rsx * nnx + _rsy * nny;
      bounceWaypoints[i + 1] = { x: hx + _rsx - 2*_rdot*nnx, y: hy + _rsy - 2*_rdot*nny };
      // Rotate all further waypoints from incoming frame into reflected frame
      for (let j = i + 2; j < bounceWaypoints.length; j++) {
        const _dx = bounceWaypoints[j].x - hx, _dy = bounceWaypoints[j].y - hy;
        const _fwd = _dx * _iux + _dy * _iuy;
        const _lat = _dx * _ipx + _dy * _ipy;
        bounceWaypoints[j] = { x: hx + _fwd * _rux + _lat * _rpx,
                               y: hy + _fwd * _ruy + _lat * _rpy };
      }
      const _deflectDist = Math.hypot(bounceWaypoints[i + 1].x - hx, bounceWaypoints[i + 1].y - hy);
      pegCollisions.push({ phase: `bounce seg${i}`, hx, hy, t: _bestT, segIdx: i, deflectDist: _deflectDist });
    }
  }
  // First bounce peg hit — used to split animation so die reaches peg before deflecting
  const _bph = pegCollisions.find(c => c.segIdx !== undefined);
  const bouncePegHit = _bph ? { segIdx: _bph.segIdx, hx: _bph.hx, hy: _bph.hy, segT: _bph.t, deflectDist: _bph.deflectDist } : null;

  // Die-die collision on bounce segments — virtual positions track where each die will end up after kicks,
  // so re-collisions (die1 hitting die2 again on a later segment) use the updated positions.
  const _vDicePos = gameState.dice.map(d => ({ x: d.x, y: d.y }));
  let bounceDieHit = null;
  let _d1SlideScale = 1.0; // scales slide distance after a bounce die-die collision
  for (let i = 0; i < bounceWaypoints.length - 1; i++) {
    const wa = bounceWaypoints[i], wb = bounceWaypoints[i + 1];
    let _bestCht = null, _bestDi = null;
    for (let _di = 0; _di < gameState.dice.length; _di++) {
      if (gameState.dice[_di].offBoard) continue;
      if (gameState.dice[_di].team && inHole(gameState.dice[_di].x, gameState.dice[_di].y, gameState.dice[_di].team)) continue;
      const _vd = _vDicePos[_di];
      const _cht = segmentCircleHit(wa.x, wa.y, wb.x, wb.y, _vd.x, _vd.y, DIE_SIZE_IN);
      if (_cht !== null && (_bestCht === null || _cht < _bestCht)) { _bestCht = _cht; _bestDi = _di; }
    }
    if (_bestCht !== null) {
      const _vd2 = _vDicePos[_bestDi];
      const hx = wa.x + _bestCht * (wb.x - wa.x), hy = wa.y + _bestCht * (wb.y - wa.y);
      const _crx = wb.x - hx, _cry = wb.y - hy;
      const _crem = Math.hypot(_crx, _cry);
      if (_crem < 0.001) continue;
      const _cdirX = _crx / _crem, _cdirY = _cry / _crem;
      const _cnnx = (_vd2.x - hx) / DIE_SIZE_IN, _cnny = (_vd2.y - hy) / DIE_SIZE_IN;
      const _cproj = _cdirX * _cnnx + _cdirY * _cnny;
      if (_cproj <= 0) continue;
      // Elastic: die1 keeps tangential (damped by DIE_TANGENTIAL_RETAIN), die2 gets normal (scaled by DIE_KICK_SCALE)
      const _ct1x = _cdirX - _cproj * _cnnx, _ct1y = _cdirY - _cproj * _cnny;
      const _ct1len = Math.hypot(_ct1x, _ct1y);
      const _d1rem = _crem * _ct1len * DIE_TANGENTIAL_RETAIN;
      const _newWbX = _d1rem > 0.001 ? hx + (_ct1x / _ct1len) * _d1rem : hx;
      const _newWbY = _d1rem > 0.001 ? hy + (_ct1y / _ct1len) * _d1rem : hy;
      // Incoming / outgoing directions for rotating subsequent waypoints
      const _idist = Math.hypot(hx - wa.x, hy - wa.y);
      const _iux = _idist > 0.001 ? (hx - wa.x) / _idist : _cdirX;
      const _iuy = _idist > 0.001 ? (hy - wa.y) / _idist : _cdirY;
      const _rux = _ct1len > 0.001 ? _ct1x / _ct1len : _cdirX;
      const _ruy = _ct1len > 0.001 ? _ct1y / _ct1len : _cdirY;
      const _ipx = -_iuy, _ipy = _iux, _rpx = -_ruy, _rpy = _rux;
      bounceWaypoints[i + 1] = { x: _newWbX, y: _newWbY };
      for (let j = i + 2; j < bounceWaypoints.length; j++) {
        const _dx = bounceWaypoints[j].x - hx, _dy = bounceWaypoints[j].y - hy;
        const _fwd = _dx * _iux + _dy * _iuy, _lat = _dx * _ipx + _dy * _ipy;
        bounceWaypoints[j] = { x: hx + _fwd * _rux + _lat * _rpx, y: hy + _fwd * _ruy + _lat * _rpy };
      }
      // Speed proxy: total remaining path (this segment tail + future bounce segments + estimated slide)
      let _totalRemPath = _crem;
      for (let j = i + 2; j < bounceWaypoints.length; j++)
        _totalRemPath += Math.hypot(bounceWaypoints[j].x - bounceWaypoints[j-1].x, bounceWaypoints[j].y - bounceWaypoints[j-1].y);
      _totalRemPath += SLIDE_MIN + speedNorm * (SLIDE_MAX - SLIDE_MIN);
      const _cd2rem = _totalRemPath * _cproj * DIE_KICK_SCALE;
      // Scale die1's future waypoint segments and slide by tangential energy fraction —
      // ensures die1's total remaining travel matches its tangential momentum (equal-reaction physics)
      const _scaleR = _ct1len * DIE_TANGENTIAL_RETAIN;
      for (let j = i + 2; j < bounceWaypoints.length; j++) {
        const _spx = bounceWaypoints[j - 1].x, _spy = bounceWaypoints[j - 1].y;
        bounceWaypoints[j] = { x: _spx + (bounceWaypoints[j].x - _spx) * _scaleR,
                               y: _spy + (bounceWaypoints[j].y - _spy) * _scaleR };
      }
      if (bounceDieHit === null) _d1SlideScale = _scaleR; // first hit sets slide scale
      let _d2toX = _vd2.x + _cnnx * _cd2rem, _d2toY = _vd2.y + _cnny * _cd2rem;
      if (gameState.dice[_bestDi].team) {
        const _dh2 = HOLES[gameState.dice[_bestDi].team];
        if (Math.hypot(_d2toX - _dh2.x, _d2toY - _dh2.y) < _dh2.r) { _d2toX = _dh2.x; _d2toY = _dh2.y; }
      }
      // Peg collision on kicked die path — stop at first peg contact
      let _kickPegHit = false;
      for (const _p of pegs) {
        const _kpt = segmentCircleHit(_vd2.x, _vd2.y, _d2toX, _d2toY, _p.x, _p.y, collideR);
        if (_kpt !== null) {
          _d2toX = _vd2.x + _kpt * (_d2toX - _vd2.x);
          _d2toY = _vd2.y + _kpt * (_d2toY - _vd2.y);
          _kickPegHit = true; break;
        }
      }
      // Update virtual position for re-collision detection on later segments
      _vDicePos[_bestDi] = { x: _d2toX, y: _d2toY };
      // First hit drives the animation split; subsequent hits just update the kicked die's final destination
      if (bounceDieHit === null) {
        bounceDieHit = { segIdx: i, hx, hy, segT: _bestCht,
          deflectDist: Math.hypot(_newWbX - hx, _newWbY - hy),
          kickedDie: { idx: _bestDi, fromX: gameState.dice[_bestDi].x, fromY: gameState.dice[_bestDi].y,
                       toX: _d2toX, toY: _d2toY, dist: _cd2rem, contactX: hx, contactY: hy, pegHit: _kickPegHit } };
      } else if (bounceDieHit.kickedDie.idx === _bestDi) {
        bounceDieHit.kickedDie.toX = _d2toX;
        bounceDieHit.kickedDie.toY = _d2toY;
        bounceDieHit.kickedDie.pegHit = bounceDieHit.kickedDie.pegHit || _kickPegHit;
      }
    }
  }

  numBounces = bounceWaypoints.length - 1;
  rollLandX = bounceWaypoints[bounceWaypoints.length - 1].x;
  rollLandY = bounceWaypoints[bounceWaypoints.length - 1].y;

  // Slide direction always tracks the actual last bounce segment (spin-steered or peg-deflected)
  { const _wpPrev = bounceWaypoints[bounceWaypoints.length - 2];
    const _dx = rollLandX - _wpPrev.x, _dy = rollLandY - _wpPrev.y;
    const _dl = Math.hypot(_dx, _dy);
    if (_dl > 0.001) {
      rollDirX = _dx / _dl; rollDirY = _dy / _dl;
      rollPerpX = -rollDirY; rollPerpY = rollDirX;
    }
  }

  // Slide: only phase that can go off-board or into the hole
  const _slideJitter = 0.85 + Math.random() * 0.15;
  const slideDist = (SLIDE_MIN + speedNorm * (SLIDE_MAX - SLIDE_MIN)) * _slideJitter * _d1SlideScale;
  const slideLat  = (Math.random() - 0.5) * 0.15 * DIE_SIZE_IN;
  let finalLandX = rollLandX + rollDirX * slideDist + rollPerpX * slideLat;
  let finalLandY = rollLandY + rollDirY * slideDist + rollPerpY * slideLat;

  // Peg collision on slide — find earliest peg hit, store split point for animation
  let slideHitX = null, slideHitY = null, slideHitT = null, pegDeflectDist = 0;
  { let _bestSt = null, _bestSPeg = null;
    for (const _p of pegs) {
      const _st = segmentCircleHit(rollLandX, rollLandY, finalLandX, finalLandY, _p.x, _p.y, collideR);
      if (_st !== null && (_bestSt === null || _st < _bestSt)) { _bestSt = _st; _bestSPeg = _p; }
    }
    if (_bestSt !== null) {
    slideHitX = rollLandX + _bestSt * (finalLandX - rollLandX);
    slideHitY = rollLandY + _bestSt * (finalLandY - rollLandY);
    slideHitT = _bestSt;
    const nx = slideHitX - _bestSPeg.x, ny = slideHitY - _bestSPeg.y, nl = Math.sqrt(nx*nx + ny*ny);
    const nnx = nx/nl, nny = ny/nl;
    const rx = finalLandX - slideHitX, ry = finalLandY - slideHitY;
    pegDeflectDist = Math.hypot(rx, ry);  // remaining slide at impact — scales with collision speed
    const dot = rx*nnx + ry*nny;
    finalLandX = slideHitX + (rx - 2*dot*nnx) * SLIDE_PEG_RESTITUTION;
    finalLandY = slideHitY + (ry - 2*dot*nny) * SLIDE_PEG_RESTITUTION;
    pegCollisions.push({ phase: 'slide', hx: slideHitX, hy: slideHitY, t: _bestSt });
    } // end if _bestSt
  } // end slide peg block

  // Die-die collision on slide path — elastic equal-mass collision, with re-collision support.
  // Virtual positions (seeded from bounce-phase results) track each die's eventual resting place.
  const _sVDice = _vDicePos.slice();  // inherit any bounce-phase virtual updates
  let dieKickT = null, kickedDie = null;
  let kiss = false;

  // Pre-peg segment check: when a peg deflection was detected, a die between rollLand and the peg
  // would be missed by the main loop (which starts from the peg hit point). Check it here first.
  if (slideHitT !== null) {
    let _preBestCht = null, _preBestDi = null;
    for (let _di = 0; _di < gameState.dice.length; _di++) {
      if (gameState.dice[_di].offBoard) continue;
      if (gameState.dice[_di].team && inHole(gameState.dice[_di].x, gameState.dice[_di].y, gameState.dice[_di].team)) continue;
      const _vd = _sVDice[_di];
      const _cht = segmentCircleHit(rollLandX, rollLandY, slideHitX, slideHitY, _vd.x, _vd.y, DIE_SIZE_IN);
      if (_cht !== null && (_preBestCht === null || _cht < _preBestCht)) { _preBestCht = _cht; _preBestDi = _di; }
    }
    if (_preBestCht !== null) {
      const _vd2 = _sVDice[_preBestDi];
      const _chx = rollLandX + _preBestCht * (slideHitX - rollLandX);
      const _chy = rollLandY + _preBestCht * (slideHitY - rollLandY);
      const _crx = slideHitX - _chx, _cry = slideHitY - _chy;
      const _crem0 = Math.hypot(_crx, _cry);
      const _postPegLen = Math.hypot(finalLandX - slideHitX, finalLandY - slideHitY);
      const _crem = _crem0 + _postPegLen; // total remaining energy (pre-peg tail + post-peg)
      const _cdirX = _crem0 > 0.001 ? _crx / _crem0 : 0, _cdirY = _crem0 > 0.001 ? _cry / _crem0 : 0;
      const _cnnx = (_vd2.x - _chx) / DIE_SIZE_IN, _cnny = (_vd2.y - _chy) / DIE_SIZE_IN;
      const _cproj = _cdirX * _cnnx + _cdirY * _cnny;
      if (_cproj > 0 && _crem >= 0.001) {
        const _ct1x = _cdirX - _cproj * _cnnx, _ct1y = _cdirY - _cproj * _cnny;
        const _ct1len = Math.hypot(_ct1x, _ct1y);
        const _cd1rem = _crem * _ct1len * DIE_TANGENTIAL_RETAIN;
        finalLandX = _cd1rem > 0.001 ? _chx + (_ct1x / _ct1len) * _cd1rem : _chx;
        finalLandY = _cd1rem > 0.001 ? _chy + (_ct1y / _ct1len) * _cd1rem : _chy;
        const _cd2rem = _crem * _cproj * DIE_KICK_SCALE;
        let _d2toX = _vd2.x + _cnnx * _cd2rem, _d2toY = _vd2.y + _cnny * _cd2rem;
        if (gameState.dice[_preBestDi].team) {
          const _dh2 = HOLES[gameState.dice[_preBestDi].team];
          if (Math.hypot(_d2toX - _dh2.x, _d2toY - _dh2.y) < _dh2.r) { _d2toX = _dh2.x; _d2toY = _dh2.y; }
        }
        let _kickPegHit = false;
        for (const _p of pegs) {
          const _kpt = segmentCircleHit(_vd2.x, _vd2.y, _d2toX, _d2toY, _p.x, _p.y, collideR);
          if (_kpt !== null) { _d2toX = _vd2.x + _kpt * (_d2toX - _vd2.x); _d2toY = _vd2.y + _kpt * (_d2toY - _vd2.y); _kickPegHit = true; break; }
        }
        _sVDice[_preBestDi] = { x: _d2toX, y: _d2toY };
        dieKickT = _preBestCht * slideHitT; // map to global slide t
        kickedDie = { idx: _preBestDi, fromX: gameState.dice[_preBestDi].x, fromY: gameState.dice[_preBestDi].y,
                      toX: _d2toX, toY: _d2toY, dist: _cd2rem, contactX: _chx, contactY: _chy, pegHit: _kickPegHit };
        // Die1 didn't reach the peg — cancel peg hit markers so animation doesn't show peg deflection
        slideHitX = null; slideHitY = null; slideHitT = null; pegDeflectDist = 0;
      }
    }
  }

  const _slideFromX = slideHitX !== null ? slideHitX : rollLandX;
  const _slideFromY = slideHitY !== null ? slideHitY : rollLandY;
  const _slideFromT = slideHitT !== null ? slideHitT : 0;
  // Two passes: first collision, then one re-collision check against updated positions
  // Skip if pre-peg check already resolved a die collision (kickedDie already set)
  for (let _pass = 0; _pass < 2 && kickedDie === null; _pass++) {
    let _bestCht = null, _bestDi = null;
    for (let _di = 0; _di < gameState.dice.length; _di++) {
      if (gameState.dice[_di].offBoard) continue;
      if (gameState.dice[_di].team && inHole(gameState.dice[_di].x, gameState.dice[_di].y, gameState.dice[_di].team)) continue;
      const _vd = _sVDice[_di];
      const _cht = segmentCircleHit(_slideFromX, _slideFromY, finalLandX, finalLandY, _vd.x, _vd.y, DIE_SIZE_IN);
      if (_cht !== null && (_bestCht === null || _cht < _bestCht)) { _bestCht = _cht; _bestDi = _di; }
    }
    if (_bestCht === null) break;
    const _vd2 = _sVDice[_bestDi];
    // Contact point
    const _chx = _slideFromX + _bestCht * (finalLandX - _slideFromX);
    const _chy = _slideFromY + _bestCht * (finalLandY - _slideFromY);
    // Remaining travel vector for die1
    const _crx = finalLandX - _chx, _cry = finalLandY - _chy;
    const _crem = Math.hypot(_crx, _cry);
    if (_crem < 0.001) break;
    // Kiss: too little energy remaining — die1 comes to rest touching die2, no kick.
    // Use square touching distance (L1 of approach normal) to avoid visual overlap at angles.
    if (_pass === 0 && _crem < KISS_THRESHOLD) {
      const _knx = (_chx - _vd2.x) / DIE_SIZE_IN, _kny = (_chy - _vd2.y) / DIE_SIZE_IN;
      const _sqDist = DIE_SIZE_IN * (Math.abs(_knx) + Math.abs(_kny));
      finalLandX = _vd2.x + _knx * _sqDist;
      finalLandY = _vd2.y + _kny * _sqDist;
      kiss = true; break;
    }
    const _cdirX = _crx / _crem, _cdirY = _cry / _crem;
    // Collision normal: from contact point toward die2 virtual center
    const _cnnx = (_vd2.x - _chx) / DIE_SIZE_IN, _cnny = (_vd2.y - _chy) / DIE_SIZE_IN;
    const _cproj = _cdirX * _cnnx + _cdirY * _cnny;
    if (_cproj <= 0) break;
    // Elastic equal-mass: die1 keeps tangential (damped), die2 gets normal (scaled)
    const _ct1x = _cdirX - _cproj * _cnnx, _ct1y = _cdirY - _cproj * _cnny;
    const _ct1len = Math.hypot(_ct1x, _ct1y);
    const _cd1rem = _crem * _ct1len * DIE_TANGENTIAL_RETAIN;
    finalLandX = _cd1rem > 0.001 ? _chx + (_ct1x / _ct1len) * _cd1rem : _chx;
    finalLandY = _cd1rem > 0.001 ? _chy + (_ct1y / _ct1len) * _cd1rem : _chy;
    const _cd2rem = _crem * _cproj * DIE_KICK_SCALE;
    let _d2toX = _vd2.x + _cnnx * _cd2rem, _d2toY = _vd2.y + _cnny * _cd2rem;
    if (gameState.dice[_bestDi].team) {
      const _dh2 = HOLES[gameState.dice[_bestDi].team];
      if (Math.hypot(_d2toX - _dh2.x, _d2toY - _dh2.y) < _dh2.r) { _d2toX = _dh2.x; _d2toY = _dh2.y; }
    }
    // Peg collision on kicked die path — stop at first peg contact
    let _kickPegHit = false;
    for (const _p of pegs) {
      const _kpt = segmentCircleHit(_vd2.x, _vd2.y, _d2toX, _d2toY, _p.x, _p.y, collideR);
      if (_kpt !== null) {
        _d2toX = _vd2.x + _kpt * (_d2toX - _vd2.x);
        _d2toY = _vd2.y + _kpt * (_d2toY - _vd2.y);
        _kickPegHit = true; break;
      }
    }
    _sVDice[_bestDi] = { x: _d2toX, y: _d2toY };
    // First hit: store for animation; second hit: update kicked die's destination
    if (_pass === 0) {
      dieKickT = _slideFromT + _bestCht * (1 - _slideFromT);
      kickedDie = { idx: _bestDi, fromX: gameState.dice[_bestDi].x, fromY: gameState.dice[_bestDi].y,
                    toX: _d2toX, toY: _d2toY, dist: _cd2rem, contactX: _chx, contactY: _chy, pegHit: _kickPegHit };
    } else if (kickedDie && kickedDie.idx === _bestDi) {
      kickedDie.toX = _d2toX; kickedDie.toY = _d2toY;
      kickedDie.pegHit = kickedDie.pegHit || _kickPegHit;
    }
  }

  // Hole gravity: pull toward hole when die center passes within hole radius during slide.
  // Pull strength scales with how far into the slide it occurs — fast die skips over, slow/stopped die gets sucked in.
  const _h = HOLES[team];
  let _hPull = 0;
  for (let _i = 0; _i <= 80; _i++) {
    const _f = _i / 80;
    const _px = rollLandX + (finalLandX - rollLandX) * _f;
    const _py = rollLandY + (finalLandY - rollLandY) * _f;
    if (Math.hypot(_h.x - _px, _h.y - _py) < _h.r) {
      const _p = Math.min(1, _f * HOLE_FUNNEL_G);  // clamped to 1 so endpoint never overshoots hole
      if (_p > _hPull) _hPull = _p;
    }
  }
  if (_hPull > 0) {
    finalLandX += (_h.x - finalLandX) * _hPull;
    finalLandY += (_h.y - finalLandY) * _hPull;
  }

  // Rotation: die pre-spins in flight then tumbles a bit on each bounce landing
  let cumAngle = (Math.random() < 0.5 ? 1 : -1) * (BOUNCE_ROT_MIN + Math.random() * (BOUNCE_ROT_MAX - BOUNCE_ROT_MIN) * 2);
  const bounceAngles = [cumAngle]; // [0] = angle at initial landing (end of flight)
  for (let i = 0; i < numBounces; i++) {
    cumAngle += rotDeltas[i];
    bounceAngles.push(cumAngle);
  }
  const finalAngle = (Math.random() * 2 - 1) * FINAL_ROT_MAX;

  return { origin, initialLandX, initialLandY, rollLandX, rollLandY, finalLandX, finalLandY, numBounces, initialBounceH, bounceWaypoints, bounceLaterals, bounceAngles, finalAngle, peg, collideR, pegCollisions, bouncePegHit, bounceDieHit, slideHitX, slideHitY, slideHitT, pegDeflectDist, dieKickT, kickedDie, kiss, slideJitter: _slideJitter };
}

// ============================================================
// CORE THROW — triggered by flick gesture
// ============================================================
function segmentCircleHit(ax, ay, bx, by, cx, cy, r) {
  // Returns t in [0,1] along AB where the circle at C with radius r is first entered, or null
  const dx = bx - ax, dy = by - ay;
  const fx = ax - cx, fy = ay - cy;
  const a  = dx*dx + dy*dy;
  if (a < 1e-10) return null;
  const b    = 2*(fx*dx + fy*dy);
  const c    = fx*fx + fy*fy - r*r;
  const disc = b*b - 4*a*c;
  if (disc < 0) return null;
  const sq = Math.sqrt(disc);
  const t1 = (-b - sq) / (2*a);
  const t2 = (-b + sq) / (2*a);
  if (t1 >= 0 && t1 <= 1) return t1;
  if (t2 >= 0 && t2 <= 1) return t2;
  return null;
}

function doThrow(dx, dy, dt) {
  if (gameState.phase !== 'throwing') return;

  const team   = gameState.currentTeam;
  const result = computeLanding(dx, dy, dt, team);
  if (!result) {
    flashStatus(`Swipe up ↑!`, 1200);
    return;
  }

  const dieValue = Math.ceil(Math.random() * 6);
  const score    = scoreDie(dieValue, result.finalLandX, result.finalLandY, team, result.finalAngle);

  // Snap to hole center if die center lands within the hole
  if (inHole(result.finalLandX, result.finalLandY, team)) {
    result.finalLandX = HOLES[team].x;
    result.finalLandY = HOLES[team].y;
  }

  // Synchronously update kicked die's score so finalizeTurn has correct data regardless of timing
  if (result.kickedDie) {
    const _kd = result.kickedDie, _kdie = gameState.dice[_kd.idx];
    if (_kdie && _kdie.team) {
      gameState.dieResults[_kd.idx] = scoreDie(_kdie.value, _kd.toX, _kd.toY, _kdie.team, _kdie.angle);
    }
  }

  gameState.phase = 'animating';

  animateDieThrow({
    startX:         result.origin.x,
    startY:         result.origin.y,
    initX:          result.initialLandX,
    initY:          result.initialLandY,
    rollX:          result.rollLandX,
    rollY:          result.rollLandY,
    endX:           result.finalLandX,
    endY:           result.finalLandY,
    numBounces:      result.numBounces,
    initialBounceH:  result.initialBounceH,
    bounceWaypoints: result.bounceWaypoints,
    bounceAngles:    result.bounceAngles,
    bouncePegHit:    result.bouncePegHit,
    bounceDieHit:    result.bounceDieHit,
    finalAngle:      result.finalAngle,
    slideHitX:       result.slideHitX,
    slideHitY:       result.slideHitY,
    slideHitT:       result.slideHitT,
    pegDeflectDist:  result.pegDeflectDist,
    dieKickT:        result.dieKickT,
    kickedDie:       result.kickedDie,
    value:           dieValue,
  }, () => {
    const labelColor = score.hanger ? '#ffdd44' : score.points > 0 ? '#44ff88' : score.points < 0 ? '#ff6644' : '#cccccc';
    gameState.dice.push({ x: result.finalLandX, y: result.finalLandY, value: dieValue, label: score.label, labelColor, offBoard: !!score.offBoard, angle: result.finalAngle, team, kiss: !!result.kiss });
    gameState.diceThrown++;
    gameState.dieResults.push(score);

    const _isHole = inHole(result.finalLandX, result.finalLandY, team);
    // if (_isHole && !score.win) playGoodThrowSound();

    if (score.win) {
      if (!gameState.redemption) gameState.scores[team] = WIN_SCORE; // show 100% / 0 to win immediately
      renderAll();
      updateUI();
      if (gameState.redemption) {
        _handleRedemptionWin();
      } else {
        triggerRedemption(team);
      }
      return;
    }

    renderAll();
    updateUI();

    const _diceNeeded = gameState.redemption ? 1 : 2;
    if (gameState.diceThrown >= _diceNeeded) {
      gameState.phase = 'showing';
      gameState._finalizeTimer = setTimeout(() => finalizeTurn(), 1800);
    } else {
      gameState.phase = 'throwing';
      updateStatus();
    }

    // Kiss is drawn on canvas via drawScoreLabel (d.kiss flag set on die object)
  });
}

// ============================================================
// CLICK — dismiss result screen only
// ============================================================
function onPointerDown(e) {
  primeAudio(); // safety-net re-attempt — no-op once the context is already running
  if (gameState.phase === 'showing') {
    finalizeTurn();
    e.preventDefault();
  }
}

// ============================================================
// THROW ZONE — die graphic + proximity gate
// ============================================================
let _lastCursorState = null;

function isNearThrowZone(screenX, screenY, r) {
  if (!r) r = document.getElementById('throw-zone').getBoundingClientRect();
  return screenX >= r.left && screenX <= r.right &&
         screenY >= r.top  && screenY <= r.bottom;
}

function refreshThrowZoneRect() { /* no-op */ }


function drawThrowDie() {
  const c   = document.getElementById('throw-die');
  if (!c) return;
  const ctx2 = c.getContext('2d');
  const W   = c.width, H = c.height;
  const pad = 4, r = 8;

  ctx2.clearRect(0, 0, W, H);

  // Body
  ctx2.fillStyle = '#FFFDF0';
  ctx2.beginPath();
  ctx2.roundRect(pad, pad, W - pad * 2, H - pad * 2, r);
  ctx2.fill();
  ctx2.strokeStyle = 'rgba(50,50,50,0.5)';
  ctx2.lineWidth = 1.5;
  ctx2.stroke();

  // 6 pip positions — drawn faint to show "any value"
  const pipR  = 3.5;
  const inner = W - pad * 2;
  const o     = inner * 0.28; // offset from center
  const cx    = W / 2, cy = H / 2;
  const pips  = [[-1,-1],[1,-1],[-1,0],[1,0],[-1,1],[1,1]];
  ctx2.fillStyle = 'rgba(30,30,30,0.18)';
  for (const [ox, oy] of pips) {
    ctx2.beginPath();
    ctx2.arc(cx + ox * o, cy + oy * o, pipR, 0, Math.PI * 2);
    ctx2.fill();
  }

  // Arrow hint: ↑
  ctx2.fillStyle = 'rgba(255,255,255,0.55)';
  ctx2.font = `bold ${Math.round(W * 0.38)}px sans-serif`;
  ctx2.textAlign = 'center';
  ctx2.textBaseline = 'middle';
  ctx2.fillText('↑', cx, cy);
}

function updateThrowZone(near) {
  const el = document.getElementById('throw-zone');
  if (!el) return;
  el.classList.toggle('ready',   near);
  el.classList.toggle('waiting', gameState.phase === 'throwing' && !near);
}

// ============================================================
// FLICK INPUT — swipe over canvas without clicking
// ============================================================
let moveHistory = [];
let flickTimer  = null;

function onPointerMove(e) {
  if (e.pointerType === 'touch') return;

  // Read rect exactly once — no DOM writes before this
  const r    = document.getElementById('throw-zone').getBoundingClientRect();
  const cx   = e.clientX, cy = e.clientY;
  const near = gameState.phase === 'throwing' && isNearThrowZone(cx, cy, r);

  // Cursor — only write when state changes to avoid reflow loop
  // (scoped to the widget, not document.body, so it doesn't affect cursor
  // elsewhere on the host page)
  if (near !== _lastCursorState) {
    document.getElementById('zonez-widget').style.cursor = near ? 'crosshair' : 'default';
    _lastCursorState = near;
  }

  // Throw zone glow
  updateThrowZone(near);

  if (gameState.phase !== 'throwing') {
    moveHistory = [];
    clearTimeout(flickTimer);
    return;
  }

  const now = Date.now();
  moveHistory.push({ x: e.clientX, y: e.clientY, t: now });
  while (moveHistory.length > 1 && now - moveHistory[0].t > 200) moveHistory.shift();

  clearTimeout(flickTimer);
  flickTimer = setTimeout(checkFlick, 75);
}

function checkFlick() {
  if (moveHistory.length < 3 || gameState.phase !== 'throwing') {
    moveHistory = [];
    return;
  }
  const oldest = moveHistory[0];
  const newest = moveHistory[moveHistory.length - 1];

  // Flick must start near the throw zone die
  if (!isNearThrowZone(oldest.x, oldest.y)) {
    moveHistory = [];
    return;
  }

  const sDx = newest.x - oldest.x;
  const sDy = newest.y - oldest.y;
  const dt  = Math.max(newest.t - oldest.t, 1);
  const speed = Math.sqrt(sDx * sDx + sDy * sDy) / dt;

  // Always require an upward screen swipe, fast enough, primarily vertical
  if (sDy > -18 || speed < 0.35 || Math.abs(sDy) < Math.abs(sDx) * 0.6) {
    moveHistory = [];
    return;
  }

  const sign = gameState.currentTeam === 'A' ? -1 : 1;
  moveHistory = [];
  doThrow(sDx * sign, sDy * sign, dt);
}

// Touch: flick on touchmove/touchend (same logic, separate path)
let touchMoveHistory = [];
let touchFlickTimer  = null;

function onTouchMove(e) {
  // These listeners are bound to `document` (not just the canvas) so a swipe
  // starting on the throw-zone icon is caught — but that means they'd also
  // fire for touches anywhere on the HOST page while the modal is closed.
  // Bail out immediately (before any preventDefault) so the rest of the
  // Shopify page always scrolls normally when the game isn't open.
  if (!document.getElementById('zonez-modal').classList.contains('open')) return;
  if (gameState.phase !== 'throwing') { touchMoveHistory = []; return; }
  const pt  = e.touches[0];
  updateThrowZone(isNearThrowZone(pt.clientX, pt.clientY));
  const now = Date.now();
  touchMoveHistory.push({ x: pt.clientX, y: pt.clientY, t: now });
  while (touchMoveHistory.length > 1 && now - touchMoveHistory[0].t > 200) touchMoveHistory.shift();
  clearTimeout(touchFlickTimer);
  touchFlickTimer = setTimeout(checkTouchFlick, 75);
  e.preventDefault();
}

function onTouchEnd(e) {
  if (!document.getElementById('zonez-modal').classList.contains('open')) return;
  primeAudio(); // safety-net re-attempt — no-op once the context is already running
  clearTimeout(touchFlickTimer);
  checkTouchFlick();
  if (gameState.phase === 'showing') finalizeTurn();
}

function checkTouchFlick() {
  if (touchMoveHistory.length < 2 || gameState.phase !== 'throwing') {
    touchMoveHistory = [];
    return;
  }
  const oldest = touchMoveHistory[0];
  const newest = touchMoveHistory[touchMoveHistory.length - 1];

  // Touch must start near the throw zone
  if (!isNearThrowZone(oldest.x, oldest.y)) { touchMoveHistory = []; return; }

  const sDx = newest.x - oldest.x;
  const sDy = newest.y - oldest.y;
  const dt  = Math.max(newest.t - oldest.t, 1);
  const speed = Math.sqrt(sDx * sDx + sDy * sDy) / dt;

  if (sDy > -15 || speed < 0.3) { touchMoveHistory = []; return; }

  const sign = gameState.currentTeam === 'A' ? -1 : 1;
  touchMoveHistory = [];
  // MOBILE_SWIPE_BOOST compensates for touch swipes covering less physical
  // distance than a trackpad/mouse flick — see the constant's comment above.
  doThrow(sDx * sign * MOBILE_SWIPE_BOOST, sDy * sign * MOBILE_SWIPE_BOOST, dt);
}

function triggerRedemption(winningTeam) {
  const otherTeam = winningTeam === 'A' ? 'B' : 'A';
  gameState.redemption = true;
  gameState.redemptionTeam = otherTeam;
  gameState.winner = winningTeam;
  gameState.currentTeam = otherTeam;
  gameState.diceThrown = 0;
  gameState.turnScore = 0;
  gameState.dieResults = [];
  gameState.dice = [];
  gameState.phase = 'throwing';
  renderAll();
  updateUI();
  applyBoardRotation();
  updateStatus();
  showNotif(`Team ${otherTeam} REDEMPTION!`);
}

function _handleRedemptionWin() {
  const LAST_LEG_START = WIN_SCORE - 10;
  const origWinner = gameState.winner;
  gameState.scores.A = LAST_LEG_START;
  gameState.scores.B = LAST_LEG_START;
  gameState.redemption = false;
  gameState.redemptionTeam = null;
  gameState.winner = null;
  gameState.currentTeam = origWinner;
  gameState.diceThrown = 0;
  gameState.turnScore = 0;
  gameState.dieResults = [];
  gameState.dice = [];
  gameState.phase = 'resetting';
  clearTimeout(gameState._finalizeTimer);
  renderAll();
  updateUI();
  updateStatus();
  showNotif('Tied Up! Replay the Last Leg.', 3500);
  gameState._finalizeTimer = setTimeout(() => {
    gameState.phase = 'throwing';
    applyBoardRotation();
    renderAll();
    updateUI();
    updateStatus();
  }, 3500);
}

function finalizeTurn() {
  clearTimeout(gameState._finalizeTimer);
  if (gameState.phase === 'gameOver' || gameState.phase === 'resetting') return;
  const team = gameState.currentTeam;
  const totalPts = gameState.dieResults.reduce((s, r) => s + r.points, 0);
  const remaining = WIN_SCORE - gameState.scores[team];

  // Redemption resolution — only 1 die was thrown per pass
  if (gameState.redemption) {
    if (totalPts === remaining) {
      // Exact hit — redemption team reaches WIN_SCORE exactly: tie, replay last leg
      _handleRedemptionWin();
      return;
    }
    if (totalPts > 0) {
      // Survived! Apply points, stay in redemption — same team throws another single die
      if (totalPts < remaining) gameState.scores[team] += totalPts;
      gameState.diceThrown = 0;
      gameState.turnScore = 0;
      gameState.dieResults = [];
      gameState.dice = [];
      gameState.phase = 'throwing';
      renderAll();
      updateUI();
      updateStatus();
    } else {
      // Score ≤ 0 — redemption failed, original winner wins
      const winningTeam = gameState.winner;
      gameState.scores[winningTeam] = WIN_SCORE;
      gameState.redemption = false;
      gameState.redemptionTeam = null;
      gameState.winner = winningTeam;
      gameState.phase = 'gameOver';
      renderAll();
      updateUI();
      showOverlay(`Team ${winningTeam} WINS!`);
    }
    return;
  }

  if (totalPts === remaining) {
    // Exact score win — give other team a redemption throw
    gameState.scores[team] = WIN_SCORE;
    renderAll();
    updateUI();
    triggerRedemption(team);
    return;
  }

  // Apply score: clamp to [0, WIN_SCORE-1]; don't advance on overshoot
  if (totalPts > 0 && totalPts < remaining) {
    gameState.scores[team] += totalPts;
  } else if (totalPts < 0) {
    // Penalty can move peg backward, floor at 0
    gameState.scores[team] = Math.max(0, gameState.scores[team] + totalPts);
  }
  // totalPts === 0 or overshoot: no change

  // Switch teams
  gameState.currentTeam = team === 'A' ? 'B' : 'A';
  gameState.diceThrown  = 0;
  gameState.turnScore   = 0;
  gameState.dieResults  = [];
  gameState.dice        = [];
  gameState.phase       = 'throwing';

  renderAll();
  updateUI();
}

// ============================================================
// UI UPDATE
// ============================================================
function applyBoardRotation() {
  // Team A sees the board rotated 180° so their scoring zone is at the top
  // and they always flick upward, same as Team B.
  canvas.style.transform = gameState.currentTeam === 'A' ? 'rotate(180deg)' : 'rotate(0deg)';
}

function updateUI() {
  document.getElementById('score-a').textContent = `${Math.round(gameState.scores.A / WIN_SCORE * 100)}%`;
  document.getElementById('score-b').textContent = `${Math.round(gameState.scores.B / WIN_SCORE * 100)}%`;
  ['A', 'B'].forEach(t => {
    const rem = WIN_SCORE - gameState.scores[t];
    const el = document.getElementById(`remain-${t.toLowerCase()}`);
    el.textContent = rem >= 0 ? `${rem} to win` : '';
  });

  // Active panel highlight
  document.getElementById('panel-a').classList.toggle('active-turn', gameState.currentTeam === 'A' && gameState.phase !== 'gameOver');
  document.getElementById('panel-b').classList.toggle('active-turn', gameState.currentTeam === 'B' && gameState.phase !== 'gameOver');

  applyBoardRotation();
  updateStatus();
  // Clear throw zone highlight during non-throwing phases
  const tz = document.getElementById('throw-zone');
  if (tz) {
    const active = gameState.phase === 'throwing';
    tz.classList.toggle('waiting', active);
    tz.classList.remove('ready');
  }
}

function updateStatus() {
  const info = document.getElementById('turn-info');
  const team = gameState.currentTeam;

  if (gameState.phase === 'gameOver') {
    info.textContent = 'Game Over';
    return;
  }
  if (gameState.phase === 'resetting') {
    info.textContent = `Both teams reset to ${WIN_SCORE - 10} — last leg restart!`;
    return;
  }
  if (gameState.phase === 'animating' || gameState.phase === 'showing') {
    const pts   = gameState.dieResults.reduce((s, r) => s + r.points, 0);
    const ptStr = pts >= 0 ? `+${pts}` : `${pts}`;
    const diceTotal = gameState.redemption ? 1 : 2;
    info.textContent = `Team ${team}'s Die ${gameState.diceThrown} of ${diceTotal}. Turn: ${ptStr}`;
    return;
  }
  if (gameState.redemption) {
    info.textContent = `REDEMPTION — Team ${team}: throw until a ≤0 result or a tie!`;
    return;
  }
  const pts = gameState.dieResults.reduce((s, r) => s + r.points, 0);
  info.textContent = gameState.diceThrown > 0
    ? `Team ${team}'s turn: ${pts >= 0 ? '+' : ''}${pts} pts`
    : `Team ${team}'s turn. Flick up ↑`;
}

let statusTimer = null;
function flashStatus(msg, ms) {
  document.getElementById('turn-info').textContent = msg;
  clearTimeout(statusTimer);
  statusTimer = setTimeout(() => updateStatus(), ms);
}


function showOverlay(msg) {
  const el = document.getElementById('overlay-msg');
  el.textContent = msg;
  el.classList.add('visible');
}

function hideOverlay() {
  clearTimeout(gameState._overlayTimer);
  const el = document.getElementById('overlay-msg');
  el.classList.remove('visible');
}

function showNotif(msg, ms) {
  clearTimeout(gameState._overlayTimer);
  const el = document.getElementById('notif-popup');
  el.textContent = msg;
  el.classList.add('visible');
  gameState._overlayTimer = setTimeout(() => el.classList.remove('visible'), ms || 3000);
}

// ============================================================
// FULL-SCREEN MODAL — open/close, scroll lock
// ============================================================
let _zonezScrollY = 0;

function openZonezModal() {
  const modal = document.getElementById('zonez-modal');
  modal.classList.add('open');
  modal.setAttribute('aria-hidden', 'false');

  // Lock the host page's scroll behind the modal (works on iOS Safari too,
  // where `overflow: hidden` on body alone isn't enough).
  _zonezScrollY = window.scrollY || window.pageYOffset || 0;
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow  = 'hidden';
  document.body.style.position  = 'fixed';
  document.body.style.top       = `-${_zonezScrollY}px`;
  document.body.style.width     = '100%';

  // The click that opened the modal is a valid user gesture — use it to
  // unlock the AudioContext immediately instead of requiring a second click.
  primeAudio();

  // Wait a frame so the modal's now-visible layout is measurable, then size
  // the board to exactly fill the available screen space.
  requestAnimationFrame(() => {
    computeLayout();
    renderAll();
    updateUI();
    refreshThrowZoneRect();
  });

  // Web font (Oswald) can swap in after that first measurement and change
  // text heights slightly — re-measure once it's actually loaded so the fit
  // stays exact instead of drifting a few px after the fact.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      if (!modal.classList.contains('open')) return;
      computeLayout();
      renderAll();
      updateUI();
      refreshThrowZoneRect();
    });
  }
}

function closeZonezModal() {
  const modal = document.getElementById('zonez-modal');
  modal.classList.remove('open');
  modal.setAttribute('aria-hidden', 'true');

  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.top      = '';
  document.body.style.width    = '';
  window.scrollTo(0, _zonezScrollY);
}

// ============================================================
// INIT
// ============================================================
function initZonez() {
  canvas = document.getElementById('board');
  ctx    = canvas.getContext('2d');

  // Overlay is absolutely positioned over canvas (CSS handles it)

  computeLayout();
  drawThrowDie();
  updateUI();
  renderAll();
  refreshThrowZoneRect();

  // Flick to throw (no click required)
  document.addEventListener('pointermove', onPointerMove);
  // Click anywhere to dismiss result screen
  canvas.addEventListener('mousedown',   onPointerDown);
  // Touch: flick gesture — bound to document (not just the canvas), same as
  // the mouse path above, since a swipe starts on the separate throw-zone
  // die icon below the canvas, not on the canvas element itself.
  document.addEventListener('touchmove', onTouchMove, { passive: false });
  document.addEventListener('touchend',  onTouchEnd,  { passive: false });

  document.getElementById('reset-btn').addEventListener('click', resetGame);
  const _instrBtn  = document.getElementById('instructions-btn');
  const _instrPanel = document.getElementById('board-annotations');
  _instrBtn.addEventListener('click', () => {
    const open = _instrPanel.classList.toggle('open');
    _instrBtn.classList.toggle('active', open);
  });
  _instrPanel.addEventListener('click', () => {
    _instrPanel.classList.remove('open');
    _instrBtn.classList.remove('active');
  });

  // Launch button (on the page) opens the full-screen modal; close button
  // and Escape both dismiss it back to the page.
  document.getElementById('zonez-launch-btn').addEventListener('click', openZonezModal);
  document.getElementById('zonez-close-btn').addEventListener('click', closeZonezModal);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.getElementById('zonez-modal').classList.contains('open')) {
      closeZonezModal();
    }
  });

  window.addEventListener('resize', () => {
    // Skip while hidden — nothing to resize, and the rect would read as 0.
    if (!document.getElementById('zonez-modal').classList.contains('open')) return;
    computeLayout(); renderAll(); updateUI(); refreshThrowZoneRect();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initZonez);
} else {
  // Custom Liquid content can be injected after DOMContentLoaded already fired
  initZonez();
}
